'use client';

import { useEffect, useMemo, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import { Card, CardContent } from '@/shared/components/ui/card';

import { Button } from '@/shared/components/ui/button';

import { Input } from '@/shared/components/ui/input';

import { Label } from '@/shared/components/ui/label';

import { Badge } from '@/shared/components/ui/badge';

import { showOverlay, hideOverlay } from '@/store/loaderSlice';

import AlreadyAppliedDialog from '@/shared/components/dialogs/AlreadyAppliedDialog';

import {

  Select,

  SelectContent,

  SelectItem,

  SelectTrigger,

  SelectValue,

} from '@/shared/components/ui/select';

import {

  Table,

  TableBody,

  TableCell,

  TableHead,

  TableHeader,

  TableRow,

} from '@/shared/components/ui/table';

import {

  Dialog,

  DialogContent,

  DialogDescription,

  DialogFooter,

  DialogHeader,

  DialogTitle,

} from '@/shared/components/ui/dialog';

import {

  Plus,

  Pencil,

  Trash2,

  Search,

  Save,

  Eye,
  EyeOff,

} from 'lucide-react';

import {
  getJobsForTransfer,
  type OwnershipAssignment,
} from '@/modules/team/api/team.api';

import {
  fetchTeamMembersThunk,
  createTeamMemberThunk,
  reactivateTeamMemberThunk,
  updateTeamMemberThunk,
  updateTeamMemberStatusThunk,
  deleteTeamMemberThunk,
} from '@/modules/team/store/teamThunk';

import {
  getReactivationTransferRequests,
  requestActiveTeamMemberTransfer,
  type ReactivationTransferRequest,
} from '@/modules/team/api/team.api';

interface TeamMember {

  id: string;

  name: string;

  email: string;

  role: string;

  post: string;

  department: string;

  location?: string;

  createdBy?: string;

  createdById?: string | null;

  status: string;

  joined: string;

  isSuperAdmin?: boolean;

  hrAdminId?: string | null;

}

interface DeletedMemberInfo {
  memberId: string;
  name: string;
  email: string;
  requestedHrAdminId?: string;
}

interface TransferJob {
  id: string;
  title: string;
  jobCode?: string | null;
  status?: string;
  isActive?: boolean;
  assignedToId?: string | null;
  createdBy?: string;
}

interface ReplacementMember {
  id: string;
  name: string;
  email: string;
  role: string;
  status: string;
}

const departments = [

  'Human Resources',

  'Talent Acquisition',

  'Operations',

  'Administration',

  'Finance',

];

const roles = [

  'HR',

  'RECRUITER',

  'OPERATIONS',

  'ADMIN',

  'MANAGER',

];

const HR_MANAGED_ROLES = [

  'RECRUITER',

  'OPERATIONS',

  'MANAGER',

];

const getCurrentUserId = (): string | null => {

  if (typeof window === 'undefined') {

    return null;

  }

  try {

    const token =

      localStorage.getItem('token') ||

      localStorage.getItem('token');

    if (!token) {

      return null;

    }

    const parts = token.split('.');

    if (parts.length !== 3) {

      return null;

    }

    const payload = JSON.parse(

      decodeURIComponent(

        atob(parts[1].replace(/-/g, '+').replace(/_/g, '/'))

          .split('')

          .map(

            (char) =>

              `%${`00${char.charCodeAt(0).toString(16)}`.slice(-2)}`

          )

          .join('')

      )

    );

    return payload?.sub || payload?.id || null;

  } catch {

    return null;

  }

};

export default function AdminTeamPage() {

  const dispatch = useDispatch<any>();

  const {

    members,

    loading: isLoading,

    saving,

    error,

  } = useSelector((state: any) => state.team);

  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [showPassword, setShowPassword] = useState(false);

  const [searchQuery, setSearchQuery] = useState('');

  const [filterRole, setFilterRole] = useState('all');

  const [filterStatus, setFilterStatus] = useState('all');

  const [teamView, setTeamView] = useState<'hrAdmins' | 'hrTeams'>('hrAdmins');

  const [showDialog, setShowDialog] = useState(false);

  const [editingMember, setEditingMember] =

    useState<TeamMember | null>(null);

  const [showDeletePopup, setShowDeletePopup] = useState(false);

  const [showActionSuccessPopup, setShowActionSuccessPopup] =

    useState(false);

  const [deleteTarget, setDeleteTarget] =

    useState<TeamMember | null>(null);

  const [showTransferDialog, setShowTransferDialog] = useState(false);
  const [transferJobs, setTransferJobs] = useState<TransferJob[]>([]);
  const [replacementMembers, setReplacementMembers] = useState<
    ReplacementMember[]
  >([]);
  const [ownershipAssignments, setOwnershipAssignments] = useState<
    Record<string, string>
  >({});
  const [loadingTransferJobs, setLoadingTransferJobs] = useState(false);

  const [successPopup, setSuccessPopup] = useState({

    title: '',

    message: '',

  });

  const [memberName, setMemberName] = useState('');

  const [memberEmail, setMemberEmail] = useState('');

  const [memberRole, setMemberRole] =

    useState<string>(roles[0]);

  const [memberPost, setMemberPost] = useState('');

  const [memberDepartment, setMemberDepartment] =

    useState<string>(departments[0]);

  const [memberLocation, setMemberLocation] =

    useState<string>('');

  const [memberExperience, setMemberExperience] =

    useState<number>(3);

  const [memberStatus, setMemberStatus] = useState('active');

  const [memberNotes, setMemberNotes] = useState('');

  const [memberPassword, setMemberPassword] = useState('');

  const [memberHrAdminId, setMemberHrAdminId] =

    useState<string>('');

  const [showReactivateDialog, setShowReactivateDialog] =

    useState(false);

  const [showActiveTransferDialog, setShowActiveTransferDialog] =
    useState(false);

  const [activeTransferMember, setActiveTransferMember] =
    useState<DeletedMemberInfo | null>(null);

  const [deletedMember, setDeletedMember] =

    useState<DeletedMemberInfo | null>(null);

  const [reactivateName, setReactivateName] =

    useState('');

  const [reactivateEmail, setReactivateEmail] =

    useState('');

  const [reactivatePassword, setReactivatePassword] =

    useState('');

  // Cross-owner reactivation request status tracking.

  const [myTransferRequests, setMyTransferRequests] =

    useState<ReactivationTransferRequest[]>([]);

  const [transferRequestsLoading, setTransferRequestsLoading] = useState(false);

  const previousTransferStatusesRef = useRef<Map<string, string>>(new Map());

  const transferStatusesInitializedRef = useRef(false);

  const refreshTransferRequests = async () => {

    try {

      setTransferRequestsLoading(true);

      const result = await getReactivationTransferRequests();

      const nextMine: ReactivationTransferRequest[] =

        Array.isArray(result?.myRequests) ? result.myRequests : [];

      if (transferStatusesInitializedRef.current) {

        const newlyDecided = nextMine.find((request) =>

          previousTransferStatusesRef.current.get(request.id) === 'PENDING' &&

          (request.status === 'APPROVED' || request.status === 'REJECTED')

        );

        if (newlyDecided?.status === 'APPROVED') {

          setSuccessPopup({

            title: 'Transfer Request Approved',

            message: `Approved by ${newlyDecided.currentHrAdminName}. ${newlyDecided.memberName} has been transferred to ${newlyDecided.requestedHrAdminName}'s team and reactivated.`,

          });

          setShowActionSuccessPopup(true);

          void dispatch(fetchTeamMembersThunk({})).unwrap().catch((refreshError: any) => {

            console.error('Failed to refresh Team Members after transfer approval:', refreshError);

          });

        } else if (newlyDecided?.status === 'REJECTED') {

          setSuccessPopup({

            title: 'Transfer Request Rejected',

            message: `${newlyDecided.currentHrAdminName} rejected the request. ${newlyDecided.memberName} remains deleted and its ownership has not changed.`,

          });

          setShowActionSuccessPopup(true);

        }

      }

      const nextStatuses = new Map<string, string>();

      nextMine.forEach((request) => nextStatuses.set(request.id, request.status));

      previousTransferStatusesRef.current = nextStatuses;

      transferStatusesInitializedRef.current = true;

      setMyTransferRequests(nextMine);

    } catch (requestError) {

      console.error('Failed to load reactivation transfer request status:', requestError);

    } finally {

      setTransferRequestsLoading(false);

    }

  };

  useEffect(() => {
    setCurrentUserId(getCurrentUserId());
    dispatch(fetchTeamMembersThunk({}));
    void refreshTransferRequests();
  }, [dispatch]);

  const hrAdmins = useMemo(() => {

    return members

      .filter(

        (member: TeamMember) =>

          member.role?.toUpperCase() === 'HR' &&

          member.status?.toLowerCase() === 'active'

      )

      .sort((a: TeamMember, b: TeamMember) =>

        a.name.localeCompare(b.name)

      );

  }, [members]);

  const isHrManagedRole = (role: string) => {

    return HR_MANAGED_ROLES.includes(role?.toUpperCase());

  };

  const getHrAdminName = (hrAdminId?: string | null) => {

    if (!hrAdminId) {

      return '-';

    }

    const hrAdmin = members.find(

      (member: TeamMember) => member.id === hrAdminId

    );

    return hrAdmin?.name || '-';

  };

  const resetDialog = () => {

    setMemberName('');

    setMemberEmail('');

    setMemberRole(roles[0]);

    setMemberPost('');

    setMemberDepartment(departments[0]);

    setMemberLocation('');

    setMemberExperience(3);

    setMemberStatus('active');

    setMemberNotes('');

    setMemberPassword('');

    setMemberHrAdminId('');

    setEditingMember(null);

    setShowPassword(false);

  };

  const resetReactivateDialog = () => {

    setDeletedMember(null);

    setReactivateName('');

    setReactivateEmail('');

    setReactivatePassword('');

  };

  const handleCreate = () => {

    resetDialog();

    setShowDialog(true);

  };

  const isCurrentUser = (member: TeamMember) => {

    return Boolean(

      currentUserId &&

      member.id &&

      currentUserId === member.id

    );

  };

  /**

   * UI ownership guard:

   *

   * ADMIN:

   * - Can manage members created by the logged-in ADMIN.

   *

   * HR:

   * - Can manage HR-managed members assigned to that HR Admin.

   *

   * The backend remains the final authorization layer.

   */

  const canManageMember = (member: TeamMember) => {

    if (isCurrentUser(member)) {

      return false;

    }

    if (!currentUserId) {

      return false;

    }

    const isOwnedByCurrentAdmin =

      member.createdById === currentUserId;

    const isOwnedByCurrentHrAdmin =

      member.hrAdminId === currentUserId &&

      isHrManagedRole(member.role);

    return (

      isOwnedByCurrentAdmin ||

      isOwnedByCurrentHrAdmin

    );

  };

  const isAdmin = (member: TeamMember) => {

    return member.role?.toLowerCase() === 'admin';

  };

  const isSuperAdmin = (member: TeamMember) => {
    return (
      member.role?.toUpperCase() === 'ADMIN' &&
      member.isSuperAdmin === true
    );
  };

  const isActive = (member: TeamMember) => {

    return member.status?.toLowerCase() === 'active';

  };

  const getStatusLabel = (status?: string) => {

    switch (status?.toLowerCase()) {

      case 'active':

        return 'Active';

      case 'on_leave':

        return 'On Leave';

      case 'inactive':

        return 'Inactive';

      default:

        return status || 'Unknown';

    }

  };

  const getStatusBadgeClass = (status?: string) => {

    switch (status?.toLowerCase()) {

      case 'active':

        return 'bg-green-500 text-white hover:bg-green-500';

      case 'on_leave':

        return 'bg-yellow-500 text-white hover:bg-yellow-500';

      default:

        return 'bg-red-500 text-white hover:bg-red-500';

    }

  };

  const activeAdminCount = useMemo(() => {

    return members.filter(

      (member: TeamMember) =>

        isAdmin(member) && isActive(member)

    ).length;

  }, [members]);

  const isLastActiveAdmin = (member: TeamMember) => {

    return (

      isAdmin(member) &&

      isActive(member) &&

      activeAdminCount <= 1

    );

  };

  const handleEdit = (member: TeamMember) => {

    if (isCurrentUser(member)) {

      window.alert(

        'You cannot update your own account.'

      );

      return;

    }

    if (!canManageMember(member)) {

      window.alert(

        'You are not authorized to manage this team member.'

      );

      return;

    }

    setEditingMember(member);

    setMemberName(member.name);

    setMemberEmail(member.email);

    setMemberRole(member.role);

    setMemberPost(member.post);

    setMemberDepartment(member.department);

    setMemberLocation(member.location || '');

    setMemberExperience(3);

    setMemberStatus(member.status);

    setMemberPassword('');

    setMemberHrAdminId(member.hrAdminId || '');

    setShowDialog(true);

  };

  const handleRoleChange = (role: string) => {

    setMemberRole(role);

    if (!isHrManagedRole(role)) {

      setMemberHrAdminId('');

    }

  };

  const getErrorMessage = (error: any) => {

    if (typeof error === 'string') {

      return error;

    }

    return (

      error?.response?.data?.message ||

      error?.payload?.message ||

      error?.message ||

      'Something went wrong. Please try again.'

    );

  };

  const handleDelete = (member: TeamMember) => {

    if (isCurrentUser(member)) {

      window.alert(

        'You cannot delete your own account.'

      );

      return;

    }

    if (!canManageMember(member)) {

      window.alert(

        'You are not authorized to manage this team member.'

      );

      return;

    }

    if (isLastActiveAdmin(member)) {

      window.alert(

        'The last active administrator cannot be deleted.'

      );

      return;

    }

    setDeleteTarget(member);
    setShowTransferDialog(true);
    setLoadingTransferJobs(true);
    setTransferJobs([]);
    setReplacementMembers([]);
    setOwnershipAssignments({});

    getJobsForTransfer(member.id)
      .then((response: any) => {
        const data = response?.data ?? response;

        const jobs: TransferJob[] = data?.jobs ?? [];
        const replacements: ReplacementMember[] =
          data?.replacementMembers ?? [];

        setTransferJobs(jobs);
        setReplacementMembers(replacements);

        // Agar jobs hain, to har job ke liye replacement select karna zaroori hai.
        if (jobs.length > 0 && replacements.length === 0) {
          window.alert(
            'No active replacement team members are available for job transfer.'
          );
        }
      })
      .catch((error: any) => {
        console.error('Failed to load jobs for transfer:', error);
        window.alert(getErrorMessage(error));
        setShowTransferDialog(false);
        setDeleteTarget(null);
      })
      .finally(() => {
        setLoadingTransferJobs(false);
      });

  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;

    const member = deleteTarget;

    if (!canManageMember(member)) {
      window.alert('You are not authorized to manage this team member.');
      return;
    }

    const missingAssignment = transferJobs.some(
      (job) => !ownershipAssignments[job.id]
    );

    if (missingAssignment) {
      window.alert(
        'Please select a replacement team member for every job.'
      );
      return;
    }

    const assignments: OwnershipAssignment[] = transferJobs.map((job) => ({
      jobId: job.id,
      assignedToId: ownershipAssignments[job.id],
    }));

    setShowTransferDialog(false);
    dispatch(showOverlay());

    try {
      await dispatch(
        deleteTeamMemberThunk({
          memberId: member.id,
          ownershipAssignments: assignments,
        })
      ).unwrap();

      await dispatch(fetchTeamMembersThunk({})).unwrap();

      setSuccessPopup({
        title: 'Team Member Deleted',
        message: `${member.name} has been deleted successfully. All assigned jobs have been transferred.`,
      });

      setShowActionSuccessPopup(true);
      setDeleteTarget(null);
      setTransferJobs([]);
      setReplacementMembers([]);
      setOwnershipAssignments({});
    } catch (error) {
      console.error('Failed to delete team member:', error);
      window.alert(getErrorMessage(error));
    } finally {
      dispatch(hideOverlay());
    }
  };

  const handleToggleStatus = async (

    member: TeamMember

  ) => {

    const newStatus =

      member.status?.toLowerCase() === 'active'

        ? 'inactive'

        : 'active';

    if (!canManageMember(member)) {

      window.alert(

        isCurrentUser(member)

          ? 'You cannot update your own account.'

          : 'You are not authorized to manage this team member.'

      );

      return;

    }

    if (

      isCurrentUser(member) &&

      newStatus === 'inactive'

    ) {

      window.alert(

        'You cannot deactivate your own account.'

      );

      return;

    }

    if (

      newStatus === 'inactive' &&

      isLastActiveAdmin(member)

    ) {

      window.alert(

        'The last active administrator cannot be deactivated.'

      );

      return;

    }

    dispatch(showOverlay());

    try {

      await dispatch(

        updateTeamMemberStatusThunk({

          memberId: member.id,

          status: newStatus,

        })

      ).unwrap();

      await dispatch(

        fetchTeamMembersThunk({})

      ).unwrap();

    } catch (error) {

      console.error(

        'Failed to update member status:',

        error

      );

      window.alert(getErrorMessage(error));

    } finally {

      dispatch(hideOverlay());

    }

  };

  const handleSave = async () => {

    if (

      editingMember &&

      isCurrentUser(editingMember)

    ) {

      window.alert(

        'You cannot update your own account.'

      );

      return;

    }

    if (

      editingMember &&

      !canManageMember(editingMember)

    ) {

      window.alert(

        'You are not authorized to manage this team member.'

      );

      return;

    }

    if (

      isHrManagedRole(memberRole) &&

      !memberHrAdminId

    ) {

      window.alert(

        'Please select an HR Admin for this team member.'

      );

      return;

    }

    if (

      isHrManagedRole(memberRole) &&

      hrAdmins.length === 0

    ) {

      window.alert(

        'No active HR Admin is available. Please create or activate an HR Admin first.'

      );

      return;

    }

    if (

      editingMember &&

      editingMember.role?.toLowerCase() === 'admin' &&

      editingMember.status?.toLowerCase() === 'active' &&

      memberStatus === 'inactive'

    ) {

      if (activeAdminCount <= 1) {

        window.alert(

          'The last active administrator cannot be deactivated.'

        );

        return;

      }

    }

    dispatch(showOverlay());

    const payload = {

      name: memberName,

      email: memberEmail,

      role: memberRole,

      post: memberPost,

      department: memberDepartment,

      location: memberLocation,

      experience: memberExperience,

      status: memberStatus,

      notes: memberNotes,

      joined:

        editingMember?.joined ??

        new Date().toISOString().slice(0, 10),

      hrAdminId: isHrManagedRole(memberRole)

        ? memberHrAdminId

        : null,

      ...(memberPassword

        ? {

          password: memberPassword,

        }

        : {}),

    };

    try {

      if (editingMember) {

        await dispatch(

          updateTeamMemberThunk({

            memberId: editingMember.id,

            updates: payload,

          })

        ).unwrap();

      } else {

        await dispatch(

          createTeamMemberThunk({

            ...payload,

            password: memberPassword,

          })

        ).unwrap();

      }
      await dispatch(
        fetchTeamMembersThunk({ force: true })
      ).unwrap();

      const wasEditing = Boolean(editingMember);

      setShowDialog(false);

      resetDialog();

      setSuccessPopup({

        title: wasEditing

          ? 'Team Member Updated'

          : 'Team Member Added',

        message: wasEditing

          ? 'The team member details have been updated successfully.'

          : 'The new team member has been added successfully.',

      });

      setShowActionSuccessPopup(true);
    } catch (error: any) {

      console.error(

        'Failed to update team member:',

        error

      );

      if (

        error?.errorCode === 'TEAM_MEMBER_PREVIOUSLY_DELETED' &&

        error?.data?.memberId

      ) {

        const deletedMemberData: DeletedMemberInfo = {
          memberId: error.data.memberId,
          name: error.data.name || memberName.trim(),
          email: error.data.email || memberEmail.trim(),
          requestedHrAdminId:
            error.data.requestedHrAdminId ||
            (isHrManagedRole(memberRole) ? memberHrAdminId : undefined),
        };

        setDeletedMember(deletedMemberData);

        setReactivateName(deletedMemberData.name);

        setReactivateEmail(deletedMemberData.email);

        setReactivatePassword('');

        setShowDialog(false);

        setShowReactivateDialog(true);

        return;

      }

      const errorCode =
        error?.errorCode ||
        error?.data?.errorCode ||
        error?.payload?.errorCode ||
        error?.response?.data?.errorCode;

      const errorData =
        error?.data ||
        error?.payload?.data ||
        error?.response?.data?.data;

      if (
        errorCode === 'TEAM_MEMBER_OWNED_BY_ANOTHER_HR' &&
        errorData?.memberId
      ) {
        setActiveTransferMember({
          memberId: errorData.memberId,
          name: errorData.name || memberName.trim(),
          email: errorData.email || memberEmail.trim(),
          requestedHrAdminId:
            errorData.requestedHrAdminId ||
            (isHrManagedRole(memberRole) ? memberHrAdminId : undefined),
        });

        setShowDialog(false);
        setShowActiveTransferDialog(true);
        return;
      }

      window.alert(getErrorMessage(error));

    } finally {

      dispatch(hideOverlay());

    }

  };

  const handleSendActiveTransferApproval = async () => {
    if (!activeTransferMember?.memberId) {
      return;
    }

    dispatch(showOverlay());

    try {
      const result = await requestActiveTeamMemberTransfer(
        activeTransferMember.memberId
      );

      setShowActiveTransferDialog(false);
      setActiveTransferMember(null);

      setSuccessPopup({
        title: 'Approval Request Sent',
        message:
          result?.message ||
          `Approval request has been sent for ${activeTransferMember.name}. The current HR Admin must approve the request before ownership changes.`,
      });

      setShowActionSuccessPopup(true);
    } catch (error: any) {
      console.error(
        'Failed to send active team member transfer request:',
        error
      );

      window.alert(getErrorMessage(error));
    } finally {
      dispatch(hideOverlay());
    }
  };

  const handleReactivate = async () => {

    if (!deletedMember?.memberId) {

      return;

    }

    if (!reactivateName.trim()) {

      window.alert('Please enter team member name.');

      return;

    }

    if (!reactivatePassword.trim()) {

      window.alert('Please enter a new password.');

      return;

    }

    if (reactivatePassword.trim().length < 6) {

      window.alert('Password must be at least 6 characters.');

      return;

    }

    dispatch(showOverlay());

    try {

      const response = await dispatch(

        reactivateTeamMemberThunk({

          memberId: deletedMember.memberId,

          name: reactivateName.trim(),

          password: reactivatePassword,

          requestedHrAdminId: deletedMember.requestedHrAdminId,

        })

      ).unwrap();

      const operationResult = response?.data ?? response;

      setShowReactivateDialog(false);

      resetReactivateDialog();

      if (operationResult?.transferRequestCreated) {

        await refreshTransferRequests();

        setSuccessPopup({

          title: 'Transfer Request Sent',

          message:

            operationResult.message ||

            'The request has been sent to the previous HR Admin. The account will transfer and reactivate automatically after approval.',

        });

        setShowActionSuccessPopup(true);

        return;

      }

      await dispatch(fetchTeamMembersThunk({})).unwrap();

      setSuccessPopup({

        title: 'Team Member Reactivated',

        message: 'The previously deleted team member has been reactivated successfully.',

      });

      setShowActionSuccessPopup(true);

    } catch (error: any) {

      console.error('Failed to reactivate team member:', error);

      window.alert(getErrorMessage(error));

    } finally {

      dispatch(hideOverlay());

    }

  };

  const viewMembers = members.filter((member: TeamMember) => {
    if (teamView === 'hrAdmins') {
      return ['HR', 'ADMIN'].includes(
        member.role?.toUpperCase() || ''
      );
    }
    return (

      HR_MANAGED_ROLES.includes(member.role?.toUpperCase()) &&

      Boolean(member.hrAdminId)

    );

  });

  const tableColumnCount = teamView === 'hrTeams' ? 9 : 8;

  const filteredMembers = viewMembers.filter(

    (member: TeamMember) => {

      const query = searchQuery.toLowerCase();

      const matchesSearch =

        member.name.toLowerCase().includes(query) ||

        member.email.toLowerCase().includes(query) ||

        member.post.toLowerCase().includes(query) ||

        member.department.toLowerCase().includes(query) ||

        (member.location || '').toLowerCase().includes(query) ||

        getHrAdminName(member.hrAdminId)

          .toLowerCase()

          .includes(query);

      const matchesRole =

        filterRole === 'all' ||

        member.role === filterRole;

      const matchesStatus =

        filterStatus === 'all' ||

        member.status === filterStatus;

      return (

        matchesSearch &&

        matchesRole &&

        matchesStatus

      );

    }

  );

  return (

    <div className="space-y-6">

      <div className="flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between">

        <div>

          <h1 className="text-2xl font-bold tracking-tight">

            Team Management

          </h1>

          <p className="text-muted-foreground">

            Manage your HR & operations team members.

          </p>

        </div>

        <Button onClick={handleCreate}>

          <Plus className="mr-2 h-4 w-4" />

          Add Team Member

        </Button>

      </div>

      {error && (

        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">

          {getErrorMessage(error)}

        </div>

      )}

      {myTransferRequests.length > 0 && (

        <Card>

          <CardContent className="space-y-4 p-4 sm:p-5">

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">

              <div>

                <h2 className="text-base font-semibold">Reactivation Transfer Requests</h2>

                <p className="mt-1 text-sm text-muted-foreground">

                  Track cross-owner requests sent to the current HR Admin for approval.

                </p>

              </div>

              <Button

                type="button"

                variant="outline"

                disabled={transferRequestsLoading}

                onClick={() => void refreshTransferRequests()}

              >

                {transferRequestsLoading ? 'Refreshing...' : 'Refresh requests'}

              </Button>

            </div>

            <div className="space-y-3">

              {myTransferRequests.slice(0, 5).map((request) => {

                const statusStyles =

                  request.status === 'APPROVED'

                    ? 'border-emerald-200 bg-emerald-50 text-emerald-800'

                    : request.status === 'REJECTED'

                      ? 'border-red-200 bg-red-50 text-red-800'

                      : 'border-amber-200 bg-amber-50 text-amber-800';

                const previousHrAdminName = request.currentHrAdminName || 'the previous HR Admin';

                const destinationHrAdminName = request.requestedHrAdminName || 'the selected HR Admin';

                const statusMessage =

                  request.status === 'APPROVED'

                    ? `Approved by ${previousHrAdminName}. The member has been transferred to ${destinationHrAdminName}'s team and reactivated.`

                    : request.status === 'REJECTED'

                      ? `Rejected by ${previousHrAdminName}. The member remains deleted and its ownership has not changed.`

                      : `Your request is awaiting approval from ${previousHrAdminName}. Once approved, the member will transfer to ${destinationHrAdminName}'s team and reactivate automatically.`;

                return (

                  <div key={request.id} className={`rounded-xl border p-3 ${statusStyles}`}>

                    <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">

                      <p className="text-sm font-semibold">

                        {request.memberName} ({request.memberEmail})

                      </p>

                      <Badge variant="outline" className="w-fit border-current bg-white/70">

                        {request.status}

                      </Badge>

                    </div>

                    <p className="mt-1 text-sm">{statusMessage}</p>

                    {request.reviewerNote && (

                      <p className="mt-1 text-xs opacity-80">Reviewer note: {request.reviewerNote}</p>

                    )}

                    <p className="mt-1 text-xs opacity-75">

                      Requested {request.createdAt ? new Date(request.createdAt).toLocaleString() : 'recently'}

                    </p>

                  </div>

                );

              })}

            </div>

          </CardContent>

        </Card>

      )}

      <Card>

        <CardContent className="pt-6">

          <div className="flex flex-col gap-4 sm:flex-row sm:items-center">

            <div className="relative flex-1">

              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />

              <Input

                placeholder="Search by name, email, post, department, location or HR Admin..."

                className="pl-10"

                value={searchQuery}

                onChange={(e) =>

                  setSearchQuery(e.target.value)

                }

              />

            </div>

            <div className="flex flex-wrap gap-2">

              <Select

                value={filterRole}

                onValueChange={setFilterRole}

              >

                <SelectTrigger className="w-[160px]">

                  <SelectValue placeholder="Role" />

                </SelectTrigger>

                <SelectContent>

                  <SelectItem value="all">

                    All Roles

                  </SelectItem>

                  {roles.map((role) => (

                    <SelectItem

                      key={role}

                      value={role}

                    >

                      {role}

                    </SelectItem>

                  ))}

                </SelectContent>

              </Select>

              <Select

                value={filterStatus}

                onValueChange={setFilterStatus}

              >

                <SelectTrigger className="w-[160px]">

                  <SelectValue placeholder="Status" />

                </SelectTrigger>

                <SelectContent>

                  <SelectItem value="all">

                    All Status

                  </SelectItem>

                  <SelectItem value="active">

                    Active

                  </SelectItem>

                  <SelectItem value="on_leave">

                    On Leave

                  </SelectItem>

                  <SelectItem value="inactive">

                    Inactive

                  </SelectItem>

                </SelectContent>

              </Select>

            </div>

          </div>

        </CardContent>

      </Card>

      <Card>

        <CardContent className="p-0">

          <div className="overflow-x-auto">

            <Table>

              <TableHeader>

                <TableRow className="border-b-0">

                  <TableHead colSpan={tableColumnCount} className="p-0">

                    <div className="flex items-center bg-muted/30">

                      <button

                        type="button"

                        onClick={() => setTeamView('hrAdmins')}

                        className={`px-4 py-3 text-sm font-medium transition-colors ${teamView === 'hrAdmins'

                          ? 'bg-background text-foreground'

                          : 'bg-transparent text-muted-foreground hover:text-foreground'

                          }`}

                      >

                        HR Admins

                      </button>

                      <span

                        aria-hidden="true"

                        className="h-5 w-px bg-border"

                      />

                      <button

                        type="button"

                        onClick={() => setTeamView('hrTeams')}

                        className={`px-4 py-3 text-sm font-medium transition-colors ${teamView === 'hrTeams'

                          ? 'bg-background text-foreground'

                          : 'bg-transparent text-muted-foreground hover:text-foreground'

                          }`}

                      >

                        HR Admin&apos;s Team

                      </button>

                    </div>

                  </TableHead>

                </TableRow>

                <TableRow>

                  <TableHead>Member</TableHead>

                  <TableHead>Role</TableHead>

                  <TableHead>Post</TableHead>

                  <TableHead>Department</TableHead>

                  <TableHead>Location</TableHead>

                  <TableHead>Created By</TableHead>

                  {teamView === 'hrTeams' && (

                    <TableHead>HR Admin</TableHead>

                  )}

                  <TableHead>Status</TableHead>

                  <TableHead className="text-right">

                    Actions

                  </TableHead>

                </TableRow>

              </TableHeader>

              <TableBody>

                {isLoading ? (

                  <TableRow>

                    <TableCell

                      colSpan={tableColumnCount}

                      className="py-10 text-center text-muted-foreground"

                    >

                      Loading team members...

                    </TableCell>

                  </TableRow>

                ) : filteredMembers.length === 0 ? (

                  <TableRow>

                    <TableCell

                      colSpan={tableColumnCount}

                      className="py-10 text-center text-muted-foreground"

                    >

                      No team members found.

                    </TableCell>

                  </TableRow>

                ) : (

                  filteredMembers.map(

                    (member: TeamMember) => {

                      const currentUser =

                        isCurrentUser(member);

                      const lastActiveAdmin =

                        isLastActiveAdmin(member);

                      const canManage =

                        canManageMember(member);

                      const disableEdit =

                        !canManage ||

                        isSuperAdmin(member) ||

                        currentUser ||

                        saving;

                      const disableBlock =

                        !canManage ||

                        isSuperAdmin(member) ||

                        currentUser ||

                        lastActiveAdmin ||

                        saving;

                      const disableDelete =

                        !canManage ||

                        isSuperAdmin(member) ||

                        currentUser ||

                        lastActiveAdmin ||

                        saving;

                      return (

                        <TableRow key={member.id}>

                          <TableCell>

                            <div className="flex flex-col gap-1">

                              <div className="flex items-center gap-2">

                                <p className="font-medium">

                                  {member.name}

                                </p>

                                {currentUser && (

                                  <Badge

                                    variant="secondary"

                                    className="text-xs"

                                  >

                                    You

                                  </Badge>

                                )}

                              </div>

                              <p className="text-xs text-muted-foreground">

                                {member.email}

                              </p>

                            </div>

                          </TableCell>

                          <TableCell>

                            {member.role}

                          </TableCell>

                          <TableCell>

                            {member.post}

                          </TableCell>

                          <TableCell>

                            {member.department}

                          </TableCell>

                          <TableCell>

                            {member.location || '-'}

                          </TableCell>

                          <TableCell>

                            {member.createdBy || '-'}

                          </TableCell>

                          {teamView === 'hrTeams' && (

                            <TableCell>

                              {isHrManagedRole(member.role)

                                ? getHrAdminName(

                                  member.hrAdminId

                                )

                                : '-'}

                            </TableCell>

                          )}

                          <TableCell>

                            <Badge

                              className={`w-fit border-0 ${getStatusBadgeClass(

                                member.status

                              )}`}

                            >

                              {getStatusLabel(

                                member.status

                              )}

                            </Badge>

                          </TableCell>

                          <TableCell className="text-right">

                            <div className="flex justify-end gap-2">


                              <Button

                                variant="ghost"

                                size="icon"

                                onClick={() =>

                                  handleEdit(member)

                                }

                                disabled={disableEdit}

                                title={

                                  currentUser

                                    ? 'You cannot update your own account'

                                    : !canManage

                                      ? 'You can manage only team members created by you'

                                      : isSuperAdmin(member)

                                        ? 'This administrator cannot be edited'

                                        : 'Edit member'

                                }

                              >

                                <Pencil

                                  className={`h-4 w-4 ${isSuperAdmin(member) ||

                                    currentUser ||

                                    !canManage

                                    ? 'text-muted-foreground'

                                    : ''

                                    }`}

                                />

                              </Button>

                              <Button

                                variant="ghost"

                                size="sm"

                                onClick={() =>

                                  handleToggleStatus(

                                    member

                                  )

                                }

                                disabled={disableBlock}

                                title={

                                  currentUser

                                    ? 'You cannot update your own account'

                                    : !canManage

                                      ? 'You can manage only team members created by you'

                                      : isSuperAdmin(member)

                                        ? 'This administrator cannot be updated'

                                        : lastActiveAdmin

                                          ? 'The last active administrator cannot be deactivated'

                                          : isActive(member)

                                            ? 'Block member'

                                            : 'Unblock member'

                                }

                                className={

                                  isSuperAdmin(member) ||

                                    currentUser ||

                                    !canManage ||

                                    lastActiveAdmin

                                    ? 'cursor-not-allowed text-muted-foreground'

                                    : isActive(member)

                                      ? 'text-red-600 hover:bg-red-50 hover:text-red-700'

                                      : 'text-green-600 hover:bg-green-50 hover:text-green-700'

                                }

                              >

                                {isActive(member)

                                  ? 'Block'

                                  : 'Unblock'}

                              </Button>

                              <Button

                                variant="ghost"

                                size="icon"

                                onClick={() =>

                                  handleDelete(member)

                                }

                                disabled={disableDelete}

                                title={

                                  currentUser

                                    ? 'You cannot delete your own account'

                                    : !canManage

                                      ? 'You can manage only team members created by you'

                                      : lastActiveAdmin

                                        ? 'The last active administrator cannot be deleted'

                                        : 'Delete member'

                                }

                              >

                                <Trash2

                                  className={`h-4 w-4 ${isSuperAdmin(member) ||

                                    currentUser ||

                                    !canManage ||

                                    lastActiveAdmin

                                    ? 'text-muted-foreground'

                                    : 'text-destructive'

                                    }`}

                                />

                              </Button>

                            </div>

                          </TableCell>

                        </TableRow>

                      );

                    }

                  )

                )}

              </TableBody>

            </Table>

          </div>

        </CardContent>

      </Card>

      <Dialog

        open={showDialog}

        onOpenChange={setShowDialog}

      >

        <DialogContent

          className="

            max-h-[90dvh]

            w-[calc(100vw-24px)]

            max-w-2xl

            overflow-y-auto

            rounded-xl

            p-4

            sm:p-6

          "

        >

          <DialogHeader className="space-y-2 text-center sm:text-left">

            <DialogTitle>

              {editingMember

                ? 'Edit Team Member'

                : 'Add Team Member'}

            </DialogTitle>

            <DialogDescription>

              {editingMember

                ? 'Update team member details.'

                : 'Create a new team member record.'}

            </DialogDescription>

          </DialogHeader>

          <div className="space-y-5 px-1 py-2">

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">

              <div className="space-y-2">

                <Label htmlFor="member-name">

                  Name *

                </Label>

                <Input

                  className="h-11"

                  id="member-name"

                  value={memberName}

                  onChange={(e) =>

                    setMemberName(e.target.value)

                  }

                  placeholder="e.g. Amit Verma"

                />

              </div>

              <div className="space-y-2">

                <Label htmlFor="member-email">

                  Email *

                </Label>

                <Input

                  className="h-11"

                  id="member-email"

                  value={memberEmail}

                  onChange={(e) =>

                    setMemberEmail(e.target.value)

                  }

                  placeholder="e.g. amit.verma@company.com"

                  type="email"

                />

              </div>

            </div>

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">

              <div className="space-y-2">

                <Label htmlFor="member-role">

                  Role

                </Label>

                <Select

                  value={memberRole}

                  onValueChange={handleRoleChange}

                >

                  <SelectTrigger className="h-11">

                    <SelectValue placeholder="Select role" />

                  </SelectTrigger>

                  <SelectContent>

                    {roles.map((role) => (

                      <SelectItem

                        key={role}

                        value={role}

                      >

                        {role}

                      </SelectItem>

                    ))}

                  </SelectContent>

                </Select>

              </div>

              <div className="space-y-2">

                <Label htmlFor="member-post">

                  Post

                </Label>

                <Input

                  className="h-11"

                  id="member-post"

                  value={memberPost}

                  onChange={(e) =>

                    setMemberPost(e.target.value)

                  }

                  placeholder="e.g. Senior Recruiter"

                  autoComplete="off"

                />

              </div>

              <div className="space-y-2">

                <Label htmlFor="member-department">

                  Department

                </Label>

                <Select

                  value={memberDepartment}

                  onValueChange={setMemberDepartment}

                >

                  <SelectTrigger className="h-11">

                    <SelectValue placeholder="Select department" />

                  </SelectTrigger>

                  <SelectContent>

                    {departments.map(

                      (department) => (

                        <SelectItem

                          key={department}

                          value={department}

                        >

                          {department}

                        </SelectItem>

                      )

                    )}

                  </SelectContent>

                </Select>

              </div>

            </div>

            {isHrManagedRole(memberRole) && (

              <div className="space-y-2">

                <Label htmlFor="member-hr-admin">

                  HR Admin *

                </Label>

                <Select

                  value={memberHrAdminId}

                  onValueChange={setMemberHrAdminId}

                >

                  <SelectTrigger

                    id="member-hr-admin"

                    className="h-11"

                  >

                    <SelectValue placeholder="Select HR Admin" />

                  </SelectTrigger>

                  <SelectContent>

                    {hrAdmins.length === 0 ? (

                      <SelectItem

                        value="__no_hr_admin__"

                        disabled

                      >

                        No active HR Admin available

                      </SelectItem>

                    ) : (

                      hrAdmins.map(

                        (hrAdmin: TeamMember) => (

                          <SelectItem

                            key={hrAdmin.id}

                            value={hrAdmin.id}

                          >

                            {hrAdmin.name} — {hrAdmin.email}

                          </SelectItem>

                        )

                      )

                    )}

                  </SelectContent>

                </Select>

                <p className="text-xs text-muted-foreground">

                  Every recruiter, operations member, and manager

                  must belong to an active HR Admin.

                </p>

              </div>

            )}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-3">

              <div className="space-y-2">

                <Label htmlFor="member-location">

                  Location

                </Label>

                <Input

                  id="member-location"

                  className="h-11"

                  value={memberLocation}

                  onChange={(e) =>

                    setMemberLocation(e.target.value)

                  }

                  placeholder="e.g. Noida"

                />

              </div>

              <div className="space-y-2">

                <Label htmlFor="member-status">

                  Status

                </Label>

                <Select

                  value={memberStatus}

                  onValueChange={setMemberStatus}

                >

                  <SelectTrigger className="h-11">

                    <SelectValue placeholder="Select status" />

                  </SelectTrigger>

                  <SelectContent>

                    <SelectItem value="active">

                      Active

                    </SelectItem>

                    <SelectItem value="on_leave">

                      On Leave

                    </SelectItem>

                    <SelectItem value="inactive">

                      Inactive

                    </SelectItem>

                  </SelectContent>

                </Select>

              </div>


              <div className="space-y-2">
                <Label htmlFor="member-password">Password</Label>

                <div className="relative">
                  <Input
                    className="h-11 pr-11 [&::-ms-reveal]:hidden [&::-ms-clear]:hidden"
                    id="member-password"
                    type={showPassword ? "text" : "password"}
                    value={memberPassword}
                    onChange={(e) => setMemberPassword(e.target.value)}
                    autoComplete="new-password"
                    placeholder={
                      editingMember
                        ? "Enter new password"
                        : "Enter password"
                    }
                  />

                  <button
                    type="button"
                    onClick={() => setShowPassword((previous) => !previous)}
                    className="absolute inset-y-0 right-0 flex w-10 items-center justify-center text-muted-foreground hover:text-foreground"
                    aria-label={showPassword ? "Hide password" : "Show password"}
                    title={showPassword ? "Hide password" : "Show password"}
                  >
                    {showPassword ? (
                      <EyeOff className="h-4 w-4" />
                    ) : (
                      <Eye className="h-4 w-4" />
                    )}
                  </button>
                </div>


              </div>


            </div>

          </div>

          <DialogFooter className="flex flex-col-reverse gap-3 sm:flex-row sm:justify-end">

            <Button

              variant="outline"

              className="h-11 w-full sm:w-auto"

              onClick={() =>

                setShowDialog(false)

              }

            >

              Cancel

            </Button>

            <Button

              onClick={handleSave}

              className="h-11 w-full sm:w-auto"

              disabled={saving}

            >

              <Save className="mr-2 h-4 w-4" />

              {editingMember

                ? 'Update Member'

                : 'Add Member'}

            </Button>

          </DialogFooter>

        </DialogContent>

      </Dialog>

      <AlreadyAppliedDialog

        open={showReactivateDialog}

        type="info"

        title="Previously Deleted Account"

        message="This email address was previously registered as a team member. Reactivate the existing account instead of creating a duplicate account."

        buttonText="Reactivate Account"

        primaryButtonDisabled={

          saving ||

          !reactivateName.trim() ||

          reactivatePassword.trim().length < 6

        }

        onOpenChange={(open) => {

          setShowReactivateDialog(open);

          if (!open) {

            resetReactivateDialog();

          }

        }}

        onViewApplicationStatus={handleReactivate}

        content={

          <div className="space-y-4">

            <div className="space-y-2">

              <Label htmlFor="reactivate-name">Name *</Label>

              <Input

                id="reactivate-name"

                className="h-11"

                value={reactivateName}

                onChange={(event) => setReactivateName(event.target.value)}

                placeholder="Enter team member name"

                autoComplete="name"

              />

            </div>

            <div className="space-y-2">

              <Label htmlFor="reactivate-email">Email</Label>

              <Input

                id="reactivate-email"

                type="email"

                className="h-11 bg-slate-50"

                value={reactivateEmail}

                readOnly

              />

              <p className="text-xs text-slate-400">

                The existing email cannot be changed during reactivation.

              </p>

            </div>

            <div className="space-y-2">

              <Label htmlFor="reactivate-password">New Password *</Label>

              <Input

                id="reactivate-password"

                type="password"

                className="h-11"

                value={reactivatePassword}

                onChange={(event) => setReactivatePassword(event.target.value)}

                placeholder="Enter new password"

                autoComplete="new-password"

              />

              <p className="text-xs text-slate-400">

                Minimum 6 characters.

              </p>

            </div>

          </div>

        }

      />

      <Dialog
        open={showActiveTransferDialog}
        onOpenChange={(open) => {
          setShowActiveTransferDialog(open);

          if (!open) {
            setActiveTransferMember(null);
          }
        }}
      >
        <DialogContent className="w-[calc(100vw-24px)] max-w-md rounded-xl">
          <DialogHeader>
            <DialogTitle>Active Team Member Already Exists</DialogTitle>

            <DialogDescription>
              This email belongs to an active team member managed by another HR
              Admin. You can request approval to transfer the member to your team.
              The existing ownership will remain unchanged until the request is
              approved.
            </DialogDescription>
          </DialogHeader>

          {activeTransferMember && (
            <div className="space-y-2 rounded-lg border p-4">
              <p className="text-sm">
                <span className="font-medium">Name: </span>
                {activeTransferMember.name}
              </p>

              <p className="break-all text-sm">
                <span className="font-medium">Email: </span>
                {activeTransferMember.email}
              </p>
            </div>
          )}

          <DialogFooter className="flex flex-col-reverse gap-2 sm:flex-row">
            <Button
              type="button"
              variant="outline"
              className="w-full sm:w-auto"
              onClick={() => {
                setShowActiveTransferDialog(false);
                setActiveTransferMember(null);
              }}
            >
              Cancel
            </Button>

            <Button
              type="button"
              className="w-full sm:w-auto"
              disabled={!activeTransferMember?.memberId}
              onClick={() => void handleSendActiveTransferApproval()}
            >
              Send Approval
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={showTransferDialog}
        onOpenChange={(open) => {
          setShowTransferDialog(open);

          if (!open) {
            setDeleteTarget(null);
            setTransferJobs([]);
            setReplacementMembers([]);
            setOwnershipAssignments({});
          }
        }}
      >
        <DialogContent className="max-h-[90dvh] w-[calc(100vw-24px)] max-w-3xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Transfer Jobs & Delete Team Member</DialogTitle>
            <DialogDescription>
              {deleteTarget
                ? `Select a replacement owner for every job assigned to ${deleteTarget.name}. The member will be deleted after the transfer succeeds.`
                : 'Select replacement owners for the jobs.'}
            </DialogDescription>
          </DialogHeader>

          {loadingTransferJobs ? (
            <p className="py-8 text-center text-muted-foreground">
              Loading jobs and eligible team members...
            </p>
          ) : (
            <div className="space-y-4">
              {transferJobs.length === 0 ? (
                <p className="rounded-md border p-4 text-sm text-muted-foreground">
                  No jobs require ownership transfer. You can proceed with deletion.
                </p>
              ) : (
                transferJobs.map((job) => (
                  <div
                    key={job.id}
                    className="grid grid-cols-1 gap-3 rounded-lg border p-4 md:grid-cols-2 md:items-center"
                  >
                    <div className="min-w-0">
                      <p className="font-medium">
                        {job.title || 'Untitled Job'}
                      </p>

                      <p className="text-sm text-muted-foreground">
                        {job.jobCode || job.id}
                        {job.status ? ` · ${job.status}` : ''}
                        {job.isActive === false ? ' · Inactive' : ''}
                      </p>
                    </div>

                    <Select
                      value={ownershipAssignments[job.id] || ''}
                      onValueChange={(memberId) =>
                        setOwnershipAssignments((previous) => ({
                          ...previous,
                          [job.id]: memberId,
                        }))
                      }
                    >
                      <SelectTrigger>
                        <SelectValue placeholder="Select replacement owner" />
                      </SelectTrigger>

                      <SelectContent>
                        {replacementMembers.map((replacement) => (
                          <SelectItem
                            key={replacement.id}
                            value={replacement.id}
                          >
                            {replacement.name} — {replacement.role}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                ))
              )}

              {transferJobs.length > 0 &&
                replacementMembers.length === 0 && (
                  <p className="text-sm text-red-600">
                    No eligible active team members are available. Deletion cannot proceed.
                  </p>
                )}
            </div>
          )}

          <DialogFooter className="gap-2">
            <Button
              type="button"
              variant="outline"
              onClick={() => {
                setShowTransferDialog(false);
                setDeleteTarget(null);
              }}
              disabled={loadingTransferJobs}
            >
              Cancel
            </Button>

            <Button
              type="button"
              variant="destructive"
              onClick={handleConfirmDelete}
              disabled={
                loadingTransferJobs ||
                (transferJobs.length > 0 &&
                  (replacementMembers.length === 0 ||
                    transferJobs.some(
                      (job) => !ownershipAssignments[job.id]
                    )))
              }
            >
              Transfer Jobs & Delete
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <AlreadyAppliedDialog

        open={showActionSuccessPopup}

        type="deleteSuccess"

        title={successPopup.title}

        message={successPopup.message}

        buttonText="Done"

        onOpenChange={setShowActionSuccessPopup}

        onViewApplicationStatus={() =>

          setShowActionSuccessPopup(false)

        }

      />

    </div>

  );

}
